"use client";

import type { Column, ColumnFilter, Table } from "@tanstack/react-table";
import {
    ArrowLeft,
    BadgeCheck,
    CalendarIcon,
    Check,
    ChevronRight,
    ListFilter,
    Loader2,
    Text,
    X,
} from "lucide-react";
import * as React from "react";

import type { DateRange } from "react-day-picker";

import { Button } from "../button";
import { Calendar } from "../calendar";
import { TimeDropdownRow } from "../calendar-with-time";
import { Checkbox } from "../checkbox";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../command";
import { Input } from "../input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../popover";
import { Slider } from "../slider";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { cn } from "../../../utils";
import { useCallbackRef } from "../../../hooks/use-callback-ref";
import {
    getDefaultOperator,
    getOperatorsForVariant,
    OPERATORS_WITHOUT_VALUE,
    type FilterOperator,
    type FilterValue,
    type FilterVariant,
} from "./data-grid-filter-utils";

// ============================================================================
// Types
// ============================================================================

/** Option returned from the backend filter options API */
export interface CommandFilterOption {
    value: string;
    label: string;
    /** Count of records matching this option (e.g., 12 employees under this manager) */
    count?: number;
}

/** Column meta configuration for command filters */
interface CommandFilterColumnMeta {
    label?: string;
    variant?: FilterVariant;
    /** Static options (for when options aren't fetched from API) */
    options?: Array<{ label: string; value: string }>;
    /** If true, this field supports fetching options from API */
    supportsOptions?: boolean;
    /** Custom operators for this column (overrides default operators for the variant) */
    operators?: ReadonlyArray<{ label: string; value: FilterOperator }>;
    /** When true and variant is "date", show a time picker alongside the calendar */
    enableTime?: boolean;
}

/** Callback to fetch options for a field */
export type FetchOptionsCallback = (
    field: string,
    query?: string
) => Promise<CommandFilterOption[]>;

// ============================================================================
// Utility
// ============================================================================

function useDebouncedCallback<T extends (...args: Parameters<T>) => ReturnType<T>>(
    callback: T,
    delay: number
): T {
    const handleCallback = useCallbackRef(callback);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

    const debouncedCallback = React.useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                handleCallback(...args);
            }, delay);
        },
        [handleCallback, delay]
    ) as T;

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedCallback;
}

function formatDate(date: Date, options?: { month?: "short" | "long" }): string {
    return date.toLocaleDateString("en-US", {
        month: options?.month ?? "short",
        day: "numeric",
        year: "numeric",
    });
}

// ============================================================================
// Constants
// ============================================================================

const FILTER_DEBOUNCE_MS = 300;
const FILTER_SHORTCUT_KEY = "f";
const REMOVE_FILTER_SHORTCUTS = ["backspace", "delete"];
const MAX_VISIBLE_LABELS = 2;

// ============================================================================
// Main Component
// ============================================================================

interface DataGridCommandFilterProps<TData>
    extends React.ComponentProps<typeof PopoverContent> {
    table: Table<TData>;
    tableName: string;
    /** Callback to fetch options for a field (required for server-side options) */
    onFetchOptions?: FetchOptionsCallback;
    /** Called when filters change */
    onFiltersChange?: (filters: ColumnFilter[]) => void;
    /** Mapping from frontend column ID to backend field name */
    columnToFieldMap?: Record<string, string>;
    disabled?: boolean;
    /** Column IDs to exclude from the command filter */
    excludeColumns?: string[];
    /**
     * Direct callback to update column filters, bypassing TanStack Table's wrapper.
     * When provided, this will be used instead of table.setColumnFilters() to avoid
     * TanStack Table's column validation filter that can remove filters for
     * columns it considers "invalid" due to stale references.
     */
    onColumnFiltersChange?: (filters: ColumnFilter[]) => void;
}

/** Pending filter selection before applying */
interface PendingFilterSelection {
    columnId: string;
    values: string[];
    labels: string[];
}

export function DataGridCommandFilter<TData>({
    table,
    tableName,
    onFetchOptions,
    onFiltersChange,
    columnToFieldMap = {},
    disabled,
    excludeColumns,
    onColumnFiltersChange,
    className,
    ...props
}: DataGridCommandFilterProps<TData>) {
    const id = React.useId();

    // Helper to update column filters - uses direct callback when available
    // to bypass TanStack Table's wrapper that can strip filters via autoRemove
    const updateColumnFilters = React.useCallback((newFilters: ColumnFilter[]) => {
        if (onColumnFiltersChange) {
            onColumnFiltersChange(newFilters);
        } else {
            table.setColumnFilters(newFilters);
        }
    }, [onColumnFiltersChange, table]);

    // Get all columns from table
    const tableColumns = table.getAllColumns();

    // Create a stable key from column IDs to detect when columns change
    // This ensures dynamic columns (e.g., scope columns loaded async) are properly tracked
    const columnsKey = tableColumns.map(c => c.id).join(',');

    // Get all filterable columns - re-computes when columns change
    const allFilterableColumns = React.useMemo(() => {
        return tableColumns.filter((column) => column.getCanFilter() && !(excludeColumns?.includes(column.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsKey, excludeColumns]);

    // Get current column filters
    const columnFilters = table.getState().columnFilters;

    // All filterable columns (don't filter out already applied ones in the new UX)
    const columns = allFilterableColumns;

    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    // Track pending filter selections (before Apply is clicked)
    const [pendingSelections, setPendingSelections] = React.useState<Map<string, PendingFilterSelection>>(new Map());

    // Track which field's value popover is open
    const [openFieldPopover, setOpenFieldPopover] = React.useState<string | null>(null);

    // Cache pre-fetched options for all fields (to show counts immediately)
    const [prefetchedOptions, setPrefetchedOptions] = React.useState<Map<string, CommandFilterOption[]>>(new Map());

    // Compute active command filters (filters with value property being object with operator)
    const commandFilters = React.useMemo(() => {
        return columnFilters.filter(f => {
            return typeof f.value === 'object' && f.value !== null && 'operator' in f.value;
        });
    }, [columnFilters]);

    // Get column labels map (use all filterable columns so we have labels for applied filters too)
    const columnLabels = React.useMemo(() => {
        const labels = new Map<string, string>();
        for (const column of allFilterableColumns) {
            const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
            labels.set(column.id, meta?.label ?? column.id);
        }
        return labels;
    }, [allFilterableColumns]);

    // Get column variants map (use all filterable columns so we have variants for applied filters too)
    const columnVariants = React.useMemo(() => {
        const variants = new Map<string, FilterVariant>();
        for (const column of allFilterableColumns) {
            const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
            variants.set(column.id, meta?.variant ?? "text");
        }
        return variants;
    }, [allFilterableColumns]);

    // Notify parent when filters change
    React.useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange(commandFilters);
        }
    }, [commandFilters, onFiltersChange]);

    // Initialize pending selections from existing filters when popover opens
    React.useEffect(() => {
        if (open) {
            const initialSelections = new Map<string, PendingFilterSelection>();
            commandFilters.forEach(filter => {
                const filterValue = filter.value as FilterValue | undefined;
                if (filterValue?.value) {
                    const values = Array.isArray(filterValue.value)
                        ? (filterValue.value as string[])
                        : [String(filterValue.value)];
                    const labels = filterValue.label
                        ? (Array.isArray(filterValue.label) ? filterValue.label : [filterValue.label])
                        : values;
                    initialSelections.set(filter.id, {
                        columnId: filter.id,
                        values,
                        labels,
                    });
                }
            });
            setPendingSelections(initialSelections);
        }
    }, [open, commandFilters]);

    // Pre-fetch options for all fields when popover opens (to show counts immediately)
    React.useEffect(() => {
        if (open && onFetchOptions) {
            const fetchAllOptions = async () => {
                const newCache = new Map<string, CommandFilterOption[]>();

                for (const column of allFilterableColumns) {
                    const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;

                    // Skip if no supportsOptions
                    if (!meta?.supportsOptions) continue;

                    try {
                        const backendField = columnToFieldMap[column.id] ?? column.id;
                        const options = await onFetchOptions(backendField);
                        newCache.set(column.id, options);
                    } catch (error) {
                        console.error(`Failed to prefetch options for ${column.id}:`, error);
                    }
                }

                setPrefetchedOptions(newCache);
            };

            fetchAllOptions();
        }
    }, [open, allFilterableColumns, onFetchOptions, columnToFieldMap]);

    // Handle opening/closing main popover
    const onOpenChange = React.useCallback((isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen) {
            setTimeout(() => {
                setInputValue("");
                setOpenFieldPopover(null);
                setPendingSelections(new Map());
            }, 100);
        }
    }, []);

    // Handle pending selection update for a field
    const onPendingSelectionChange = React.useCallback((columnId: string, values: string[], labels: string[]) => {
        setPendingSelections(prev => {
            const newSelections = new Map(prev);
            if (values.length === 0) {
                newSelections.delete(columnId);
            } else {
                newSelections.set(columnId, { columnId, values, labels });
            }
            return newSelections;
        });
    }, []);

    // Apply all pending selections
    const onApplyFilters = React.useCallback(() => {
        const newFilters: ColumnFilter[] = [];

        // Keep non-command filters (simple values from search bar)
        const existingNonCommandFilters = columnFilters.filter(f => {
            return typeof f.value !== 'object' || f.value === null || !('operator' in f.value);
        });
        newFilters.push(...existingNonCommandFilters);

        // Add command filters from pending selections
        pendingSelections.forEach((selection, columnId) => {
            if (selection.values.length > 0) {
                const variant = (columnVariants.get(columnId) ?? "text") as FilterVariant;
                const defaultOperator = getDefaultOperator(variant);
                const isMultiSelectOperator = defaultOperator === "isAnyOf" || defaultOperator === "isNoneOf";

                // Handle date range: value may be "YYYY-MM-DD,YYYY-MM-DD"
                if ((variant === "date" || variant === "dateRange") && selection.values[0]?.includes(',')) {
                    const [startDate, endDate] = selection.values[0].split(',');
                    newFilters.push({
                        id: columnId,
                        value: {
                            operator: "isBetween",
                            value: startDate,
                            endValue: endDate,
                            label: selection.labels[0],
                        },
                    });
                } else if (variant === "range") {
                    // Parse optional dimension suffix: "value|employer" or "value|employee"
                    const rawVal = selection.values[0];
                    const pipeIdx = rawVal.lastIndexOf("|");
                    const dimStr = pipeIdx !== -1 ? rawVal.slice(pipeIdx + 1) : undefined;
                    const dimension = (dimStr === "employer" || dimStr === "employee") ? dimStr : undefined;
                    const cleanVal = dimension ? rawVal.slice(0, pipeIdx) : rawVal;

                    if (cleanVal.includes(",")) {
                        // Numeric range: "min,max"
                        const [min, max] = cleanVal.split(",").map(Number);
                        newFilters.push({
                            id: columnId,
                            value: {
                                operator: "isBetween" as FilterOperator,
                                value: min || 0,
                                endValue: max || 0,
                                label: selection.labels[0],
                                ...(dimension && { dimension }),
                            },
                        });
                    } else if (cleanVal === "enrolled") {
                        newFilters.push({
                            id: columnId,
                            value: {
                                operator: "greaterThan" as FilterOperator,
                                value: 0,
                                label: selection.labels[0],
                                ...(dimension && { dimension }),
                            },
                        });
                    } else if (cleanVal === "not_enrolled") {
                        newFilters.push({
                            id: columnId,
                            value: {
                                operator: "lessThanOrEqual" as FilterOperator,
                                value: 0,
                                label: selection.labels[0],
                                ...(dimension && { dimension }),
                            },
                        });
                    } else {
                        // Fallback
                        newFilters.push({
                            id: columnId,
                            value: {
                                operator: defaultOperator,
                                value: selection.values[0],
                                label: selection.labels[0],
                            },
                        });
                    }
                } else {
                    // When user selected multiple values, always use isAnyOf
                    // regardless of the column's default operator (e.g., text columns default to "contains"
                    // but multi-checkbox selection should use "isAnyOf" / IN)
                    const hasMultipleValues = selection.values.length > 1;
                    const effectiveOperator = (isMultiSelectOperator || hasMultipleValues) ? "isAnyOf" as FilterOperator : defaultOperator;
                    const useArray = isMultiSelectOperator || hasMultipleValues;

                    newFilters.push({
                        id: columnId,
                        value: {
                            operator: effectiveOperator,
                            value: useArray ? selection.values : selection.values[0],
                            label: useArray ? selection.labels : selection.labels[0],
                        },
                    });
                }
            }
        });

        updateColumnFilters(newFilters);
        setOpen(false);
    }, [table, columnFilters, pendingSelections, columnVariants, updateColumnFilters]);

    // Remove a filter
    const onFilterRemove = React.useCallback(
        (filterId: string) => {
            const prevFilters = table.getState().columnFilters;
            const newFilters = prevFilters.filter(f => f.id !== filterId);
            updateColumnFilters(newFilters);
            requestAnimationFrame(() => {
                triggerRef.current?.focus();
            });
        },
        [table, updateColumnFilters]
    );

    // Update a filter
    const onFilterUpdate = React.useCallback(
        (filterId: string, updates: Partial<ColumnFilter>) => {
            const prevFilters = table.getState().columnFilters;
            const newFilters = prevFilters.map(filter =>
                filter.id === filterId ? { ...filter, ...updates } : filter
            );
            updateColumnFilters(newFilters);
        },
        [table, updateColumnFilters]
    );

    // Reset all command filters
    const onFiltersReset = React.useCallback(() => {
        const prevFilters = table.getState().columnFilters;
        // Keep non-command filters (simple values from search bar)
        const newFilters = prevFilters.filter(f => {
            return typeof f.value !== 'object' || f.value === null || !('operator' in f.value);
        });
        updateColumnFilters(newFilters);
    }, [table, updateColumnFilters]);

    // Keyboard shortcut to open (Cmd+Shift+F)
    React.useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement ||
                (event.target instanceof HTMLElement && event.target.contentEditable === "true")
            ) {
                return;
            }

            if (
                event.key.toLowerCase() === FILTER_SHORTCUT_KEY &&
                (event.ctrlKey || event.metaKey) &&
                event.shiftKey
            ) {
                event.preventDefault();
                setOpen(prev => !prev);
            }
        }

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Handle trigger keydown (backspace to remove last filter)
    const onTriggerKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (
                REMOVE_FILTER_SHORTCUTS.includes(event.key.toLowerCase()) &&
                commandFilters.length > 0
            ) {
                event.preventDefault();
                onFilterRemove(commandFilters[commandFilters.length - 1]?.id ?? "");
            }
        },
        [commandFilters, onFilterRemove]
    );

    // Count total pending selections
    const pendingCount = React.useMemo(() => {
        let count = 0;
        pendingSelections.forEach(selection => {
            count += selection.values.length;
        });
        return count;
    }, [pendingSelections]);

    // Refs for flyout submenu positioning
    const containerRef = React.useRef<HTMLDivElement>(null);
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const flyoutRef = React.useRef<HTMLDivElement>(null);
    const fieldRowRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
    const [submenuTop, setSubmenuTop] = React.useState(0);
    const [flyoutSide, setFlyoutSide] = React.useState<'right' | 'left'>('right');

    const updateSubmenuPosition = React.useCallback(() => {
        if (openFieldPopover && containerRef.current) {
            const rowEl = fieldRowRefs.current.get(openFieldPopover);
            if (rowEl) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const rowRect = rowEl.getBoundingClientRect();
                let top = rowRect.top - containerRect.top;

                // Clamp flyout within viewport bounds
                const flyoutWidth = flyoutRef.current?.getBoundingClientRect().width ?? 280;
                const flyoutHeight = flyoutRef.current?.getBoundingClientRect().height
                    ?? Math.min(400, window.innerHeight * 0.7);
                const viewportPadding = 8;
                const flyoutBottomVp = containerRect.top + top + flyoutHeight;

                // Flip to left side if flyout would overflow right edge of viewport
                const rightEdge = containerRect.right + flyoutWidth + viewportPadding;
                if (rightEdge > window.innerWidth) {
                    setFlyoutSide('left');
                } else {
                    setFlyoutSide('right');
                }

                // Shift up if overflows bottom of viewport
                if (flyoutBottomVp > window.innerHeight - viewportPadding) {
                    top -= flyoutBottomVp - (window.innerHeight - viewportPadding);
                }
                // Shift down if overflows top of viewport
                if (containerRect.top + top < viewportPadding) {
                    top = viewportPadding - containerRect.top;
                }

                setSubmenuTop(top);
            }
        }
    }, [openFieldPopover]);

    React.useEffect(() => {
        updateSubmenuPosition();
    }, [updateSubmenuPosition]);

    // Re-clamp after flyout renders so we use the actual measured height
    React.useEffect(() => {
        if (openFieldPopover && flyoutRef.current) {
            requestAnimationFrame(updateSubmenuPosition);
        }
    }, [openFieldPopover, updateSubmenuPosition]);

    // No scroll listener needed — flyout stays fixed at its initial position
    // and closes only when the user clicks a different field or outside

    // Sort and filter columns - dropdown fields first, then text fields
    const filteredColumns = React.useMemo(() => {
        // Helper to check if a column has dropdown options
        const hasDropdownOptions = (column: Column<TData>) => {
            const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
            return meta?.supportsOptions || meta?.options || meta?.variant === "boolean";
        };

        // Sort: dropdown fields first, then text fields
        const sortedColumns = [...columns].sort((a, b) => {
            const aHasOptions = hasDropdownOptions(a);
            const bHasOptions = hasDropdownOptions(b);
            if (aHasOptions && !bHasOptions) return -1;
            if (!aHasOptions && bHasOptions) return 1;
            return 0;
        });

        // Apply search filter if needed
        if (!inputValue.trim()) return sortedColumns;
        const search = inputValue.toLowerCase();
        return sortedColumns.filter(column => {
            const label = columnLabels.get(column.id) ?? column.id;
            return label.toLowerCase().includes(search);
        });
    }, [columns, inputValue, columnLabels]);

    return (
        <TooltipProvider>
            <div role="list" className="flex flex-wrap items-center gap-2">
                {/* Active filter pills */}
                {commandFilters.map((filter) => (
                    <CommandFilterItem
                        key={filter.id}
                        filter={filter}
                        filterItemId={`${id}-filter-${filter.id}`}
                        columns={allFilterableColumns}
                        columnLabels={columnLabels}
                        columnVariants={columnVariants}
                        table={table}
                        onFilterUpdate={onFilterUpdate}
                        onFilterRemove={onFilterRemove}
                        onFetchOptions={onFetchOptions}
                        columnToFieldMap={columnToFieldMap}
                    />
                ))}

                {/* Reset all button - only show when there are 2+ filters */}
                {commandFilters.length > 1 && (
                    <Button
                        aria-label="Reset all filters"
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={onFiltersReset}
                    >
                        <X />
                    </Button>
                )}

            {/* Filter trigger button + popover */}
            <Popover open={open} onOpenChange={onOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        aria-label="Open filter command menu"
                        variant="outline"
                        size={commandFilters.length > 0 ? "icon" : "sm"}
                        className={cn(commandFilters.length > 0 && "size-8", "h-8 font-normal")}
                        ref={triggerRef}
                        onKeyDown={onTriggerKeyDown}
                        disabled={disabled}
                    >
                        <ListFilter className="text-muted-foreground" />
                        {commandFilters.length > 0 ? null : "Filter"}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className={cn("p-0 bg-white overflow-visible", className)}
                    align="start"
                    collisionPadding={16}
                    {...props}
                >
                    <div ref={containerRef} className="relative flex flex-col">
                        {/* Search input */}
                        <div className="border-b px-3 py-2">
                            <Input
                                autoFocus
                                placeholder="Search fields..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                className="h-8 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none"
                            />
                        </div>

                        {/* Field list */}
                        <div ref={scrollRef} className="max-h-[300px] overflow-y-auto py-1">
                            {filteredColumns.length === 0 ? (
                                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                    No fields found.
                                </div>
                            ) : (
                                filteredColumns.map((column) => {
                                    const label = columnLabels.get(column.id) ?? column.id;
                                    const pending = pendingSelections.get(column.id);
                                    const selectedCount = pending?.values.length ?? 0;
                                    const prefetched = prefetchedOptions.get(column.id);
                                    const summaryText = selectedCount === 0 ? ""
                                        : selectedCount === 1 ? (pending?.labels[0] ?? pending?.values[0] ?? "")
                                        : selectedCount <= 2 ? (pending?.labels.join(", ") ?? "")
                                        : `${pending?.labels.slice(0, 2).join(", ")} +${selectedCount - 2}`;

                                    return (
                                        <FilterFieldRow
                                            key={column.id}
                                            ref={(el: HTMLButtonElement | null) => {
                                                if (el) fieldRowRefs.current.set(column.id, el);
                                                else fieldRowRefs.current.delete(column.id);
                                            }}
                                            label={label}
                                            selectedCount={selectedCount}
                                            totalOptionsCount={prefetched?.length ?? 0}
                                            summaryText={summaryText}
                                            isActive={openFieldPopover === column.id}
                                            onClick={() => setOpenFieldPopover(
                                                openFieldPopover === column.id ? null : column.id
                                            )}
                                        />
                                    );
                                })
                            )}
                        </div>

                        {/* Apply button */}
                        <div className="border-t px-3 py-2">
                            <Button
                                size="sm"
                                className="w-full"
                                onClick={onApplyFilters}
                            >
                                Apply {pendingCount > 0 && `(${pendingCount})`}
                            </Button>
                        </div>

                        {/* Flyout submenu - positioned at the clicked row, clamped to viewport */}
                        {openFieldPopover && allFilterableColumns.find(c => c.id === openFieldPopover) && (
                            <div
                                ref={flyoutRef}
                                className={cn(
                                    "absolute top-0",
                                    flyoutSide === 'right' ? "left-full ml-1" : "right-full mr-1"
                                )}
                                style={{ top: submenuTop }}
                            >
                                <div className="rounded-lg border bg-white shadow-lg overflow-hidden">
                                    <FilterOptionsPanel
                                        key={openFieldPopover}
                                        column={allFilterableColumns.find(c => c.id === openFieldPopover)!}
                                        label={columnLabels.get(openFieldPopover) ?? openFieldPopover}
                                        selectedValues={pendingSelections.get(openFieldPopover)?.values ?? []}
                                        selectedLabels={pendingSelections.get(openFieldPopover)?.labels ?? []}
                                        onSelectionChange={(values, labels) => {
                                            onPendingSelectionChange(openFieldPopover, values, labels);
                                        }}
                                        variant={columnVariants.get(openFieldPopover) ?? "text"}
                                        onFetchOptions={onFetchOptions}
                                        columnToFieldMap={columnToFieldMap}
                                        prefetchedOptions={prefetchedOptions.get(openFieldPopover)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
            </div>
        </TooltipProvider>
    );
}

// ============================================================================
// Filter Field Row (simple button - drill-down handled by parent)
// ============================================================================

interface FilterFieldRowProps {
    label: string;
    selectedCount: number;
    totalOptionsCount: number;
    summaryText: string;
    isActive: boolean;
    onClick: () => void;
}

const FilterFieldRow = React.forwardRef<HTMLButtonElement, FilterFieldRowProps>(
function FilterFieldRow({ label, selectedCount, totalOptionsCount, summaryText, isActive, onClick }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent cursor-pointer transition-colors",
                isActive && "bg-accent",
                !isActive && selectedCount > 0 && "bg-accent/50"
            )}
            onClick={onClick}
        >
            <div className="flex items-center gap-2">
                <span className="font-medium">{label}</span>
                {totalOptionsCount > 0 && (
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                        {totalOptionsCount}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                {selectedCount > 0 && (
                    <span className="text-xs text-muted-foreground max-w-[120px] truncate">
                        {summaryText}
                    </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
        </button>
    );
});

// ============================================================================
// Filter Options Panel (drill-down view for selecting field values)
// ============================================================================

interface FilterOptionsPanelProps<TData> {
    column: Column<TData>;
    label: string;
    selectedValues: string[];
    selectedLabels: string[];
    onSelectionChange: (values: string[], labels: string[]) => void;
    variant: FilterVariant;
    onFetchOptions?: FetchOptionsCallback;
    columnToFieldMap: Record<string, string>;
    prefetchedOptions?: CommandFilterOption[];
}

function FilterOptionsPanel<TData>({
    column,
    label,
    selectedValues,
    selectedLabels,
    onSelectionChange,
    variant,
    onFetchOptions,
    columnToFieldMap,
    prefetchedOptions,
}: FilterOptionsPanelProps<TData>) {
    const [options, setOptions] = React.useState<CommandFilterOption[]>(prefetchedOptions ?? []);
    const [loading, setLoading] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");

    // Workaround: Dialog's react-remove-scroll blocks wheel events on elements
    // outside the Dialog content DOM tree. The flyout is positioned outside the
    // Popover wrapper (via absolute left-full), so we must manually handle scroll.
    const optionsScrollRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const el = optionsScrollRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollHeight <= clientHeight) return; // not scrollable
            // Clamp scroll to bounds and prevent Dialog from swallowing the event
            const maxScroll = scrollHeight - clientHeight;
            const newScroll = Math.max(0, Math.min(maxScroll, scrollTop + e.deltaY));
            if (newScroll !== scrollTop) {
                el.scrollTop = newScroll;
                e.preventDefault();
                e.stopPropagation();
            }
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
    const supportsServerSearch = !!meta?.supportsOptions;
    const hasOptions = supportsServerSearch || !!meta?.options || variant === "boolean";

    // Fetch options
    const fetchOptions = React.useCallback(async (query?: string) => {
        if (variant === "boolean") {
            setOptions([
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
            ]);
            return;
        }

        if (meta?.options && !supportsServerSearch) {
            setOptions(meta.options.map(o => ({ value: o.value, label: o.label })));
            return;
        }

        if (supportsServerSearch && onFetchOptions) {
            setLoading(true);
            try {
                const backendField = columnToFieldMap[column.id] ?? column.id;
                const fetched = await onFetchOptions(backendField, query);
                setOptions(fetched);
            } catch (error) {
                console.error("Failed to fetch filter options:", error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }
    }, [column.id, variant, meta?.options, supportsServerSearch, onFetchOptions, columnToFieldMap]);

    const debouncedFetch = useDebouncedCallback((query: string) => {
        fetchOptions(query);
    }, FILTER_DEBOUNCE_MS);

    // Fetch on mount if not prefetched
    React.useEffect(() => {
        if (!prefetchedOptions && hasOptions) {
            fetchOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOptions]);

    // Handle search
    const handleSearch = React.useCallback((value: string) => {
        setSearchValue(value);
        if (supportsServerSearch) {
            debouncedFetch(value);
        }
    }, [supportsServerSearch, debouncedFetch]);

    // Handle toggle
    const handleToggle = React.useCallback((optionValue: string, optionLabel: string) => {
        const idx = selectedValues.indexOf(optionValue);
        if (idx >= 0) {
            const newValues = selectedValues.filter(v => v !== optionValue);
            const newLabels = selectedLabels.filter((_, i) => i !== idx);
            onSelectionChange(newValues, newLabels);
        } else {
            onSelectionChange([...selectedValues, optionValue], [...selectedLabels, optionLabel]);
        }
    }, [selectedValues, selectedLabels, onSelectionChange]);

    // Client-side filter for non-server search
    const filteredOptions = React.useMemo(() => {
        if (!searchValue.trim() || supportsServerSearch) return options;
        const search = searchValue.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(search));
    }, [options, searchValue, supportsServerSearch]);

    // Range variant — show min/max dollar inputs (+ enrollment for benefits)
    if (variant === "range") {
        return (
            <RangeInputPanel
                label={label}
                column={column}
                selectedValues={selectedValues}
                onSelectionChange={onSelectionChange}
            />
        );
    }

    // Date variant - render calendar picker in the submenu
    // Selections auto-update as the user picks dates; the main Apply button commits them.
    if (variant === "date" || variant === "dateRange") {
        const enableTime = !!meta?.enableTime;

        const formatDateStr = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const formatDisplayDate = (date: Date): string => {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };

        const format12h = (h24: number, m: number): string => {
            const ampm = h24 < 12 ? "AM" : "PM";
            const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
            return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
        };

        const formatDateTimeStr = (date: Date, h: number, m: number): string => {
            return `${formatDateStr(date)}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
        };

        const parseExisting = (): DateRange | undefined => {
            if (selectedValues.length === 0) return undefined;
            const val = selectedValues[0];
            if (val.includes(',')) {
                const [start, end] = val.split(',');
                const from = new Date(start);
                const to = new Date(end);
                if (!isNaN(from.getTime())) return { from, to: isNaN(to.getTime()) ? undefined : to };
            }
            const d = new Date(val);
            return isNaN(d.getTime()) ? undefined : { from: d, to: undefined };
        };

        const parseExistingTime = (index: 0 | 1): { h: number; m: number } => {
            if (selectedValues.length === 0) return index === 0 ? { h: 0, m: 0 } : { h: 23, m: 59 };
            const val = selectedValues[0];
            const parts = val.split(',');
            const dateStr = parts[index] ?? parts[0];
            if (dateStr?.includes('T')) {
                const timePart = dateStr.split('T')[1];
                const [hStr, mStr] = timePart.split(':');
                return { h: parseInt(hStr, 10) || 0, m: parseInt(mStr, 10) || 0 };
            }
            return index === 0 ? { h: 0, m: 0 } : { h: 23, m: 59 };
        };

        const [dateRange, setDateRange] = React.useState<DateRange | undefined>(parseExisting);
        const [startTimeH, setStartTimeH] = React.useState(parseExistingTime(0).h);
        const [startTimeM, setStartTimeM] = React.useState(parseExistingTime(0).m);
        const [endTimeH, setEndTimeH] = React.useState(parseExistingTime(1).h);
        const [endTimeM, setEndTimeM] = React.useState(parseExistingTime(1).m);

        const emitSelection = React.useCallback((range: DateRange | undefined, sH: number, sM: number, eH: number, eM: number) => {
            if (range?.from) {
                const startDate = enableTime ? formatDateTimeStr(range.from, sH, sM) : formatDateStr(range.from);
                const endDateObj = range.to ?? range.from;
                const endDate = enableTime ? formatDateTimeStr(endDateObj, eH, eM) : formatDateStr(endDateObj);
                const displayStart = enableTime
                    ? `${formatDisplayDate(range.from)} ${format12h(sH, sM)}`
                    : formatDisplayDate(range.from);
                const displayEnd = range.to
                    ? (enableTime
                        ? `${formatDisplayDate(range.to)} ${format12h(eH, eM)}`
                        : formatDisplayDate(range.to))
                    : null;
                const displayLabel = displayEnd ? `${displayStart} - ${displayEnd}` : displayStart;
                onSelectionChange([`${startDate},${endDate}`], [displayLabel]);
            } else {
                onSelectionChange([], []);
            }
        }, [enableTime, onSelectionChange]);

        const handleDateSelect = React.useCallback((range: DateRange | undefined) => {
            setDateRange(range);
            emitSelection(range, startTimeH, startTimeM, endTimeH, endTimeM);
        }, [emitSelection, startTimeH, startTimeM, endTimeH, endTimeM]);

        const handleStartHourChange = React.useCallback((h: number) => {
            setStartTimeH(h);
            emitSelection(dateRange, h, startTimeM, endTimeH, endTimeM);
        }, [emitSelection, dateRange, startTimeM, endTimeH, endTimeM]);

        const handleStartMinChange = React.useCallback((m: number) => {
            setStartTimeM(m);
            emitSelection(dateRange, startTimeH, m, endTimeH, endTimeM);
        }, [emitSelection, dateRange, startTimeH, endTimeH, endTimeM]);

        const handleEndHourChange = React.useCallback((h: number) => {
            setEndTimeH(h);
            emitSelection(dateRange, startTimeH, startTimeM, h, endTimeM);
        }, [emitSelection, dateRange, startTimeH, startTimeM, endTimeM]);

        const handleEndMinChange = React.useCallback((m: number) => {
            setEndTimeM(m);
            emitSelection(dateRange, startTimeH, startTimeM, endTimeH, m);
        }, [emitSelection, dateRange, startTimeH, startTimeM, endTimeH]);

        const handleClear = () => {
            setDateRange(undefined);
            setStartTimeH(0);
            setStartTimeM(0);
            setEndTimeH(23);
            setEndTimeM(59);
            onSelectionChange([], []);
        };

        return (
            <div className="w-auto flex-shrink-0 flex flex-col bg-white">
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="font-medium text-sm truncate">{label}</span>
                    {selectedValues.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="p-2">
                    {enableTime && (
                        <div className="flex flex-col gap-2 mb-2 pb-2 border-b border-border">
                            <TimeDropdownRow label="From" h24={startTimeH} min={startTimeM} onHourChange={handleStartHourChange} onMinuteChange={handleStartMinChange} />
                            <TimeDropdownRow label="To" h24={endTimeH} min={endTimeM} onHourChange={handleEndHourChange} onMinuteChange={handleEndMinChange} />
                        </div>
                    )}
                    <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={handleDateSelect}
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={2100}
                        width={enableTime ? "100%" : undefined}
                        initialFocus
                        numberOfMonths={1}
                    />
                    <p className="text-xs text-muted-foreground mt-2 px-1">
                        {dateRange?.from && dateRange?.to
                            ? `${formatDateStr(dateRange.from)} to ${formatDateStr(dateRange.to)}`
                            : dateRange?.from
                                ? `${formatDateStr(dateRange.from)} (select end date)`
                                : "Select date range"}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-[260px] max-h-[min(400px,70vh)] flex-shrink-0 flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="font-medium text-sm truncate">{label}</span>
                {selectedValues.length > 0 && (
                    <span className="text-xs text-primary font-medium flex-shrink-0 ml-2">
                        {selectedValues.length} selected
                    </span>
                )}
            </div>

            {hasOptions ? (
                <>
                    {/* Search input */}
                    <div className="border-b px-3 py-2">
                        <Input
                            autoFocus
                            placeholder="Search..."
                            value={searchValue}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="h-8 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none"
                        />
                    </div>

                    {/* Options list */}
                    <div ref={optionsScrollRef} className="flex-1 min-h-0 overflow-y-auto py-1">
                        {loading ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                No options found.
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = selectedValues.includes(option.value);
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={cn(
                                            "flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer transition-colors",
                                            isSelected && "bg-accent/50"
                                        )}
                                        onClick={() => handleToggle(option.value, option.label)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            className="pointer-events-none"
                                            tabIndex={-1}
                                        />
                                        <span className="truncate flex-1 text-left">{option.label}</span>
                                        {option.count !== undefined && (
                                            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                                {option.count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </>
            ) : (
                /* Text input for fields without options */
                <div className="p-3">
                    <Input
                        autoFocus
                        placeholder="Type a value..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && searchValue.trim()) {
                                handleToggle(searchValue.trim(), searchValue.trim());
                                setSearchValue("");
                            }
                        }}
                        className="h-8"
                    />
                    {selectedValues.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {selectedValues.map((value, index) => (
                                <span
                                    key={value}
                                    className="inline-flex items-center gap-0.5 bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs"
                                >
                                    {selectedLabels[index] ?? value}
                                    <button
                                        type="button"
                                        onClick={() => handleToggle(value, selectedLabels[index] ?? value)}
                                        className="ml-0.5 rounded-sm opacity-60 hover:opacity-100"
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                        Press Enter to add value
                    </p>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Filter Value Selector (second step of command filter)
// ============================================================================

interface FilterValueSelectorProps<TData> {
    column: Column<TData>;
    value: string;
    options: CommandFilterOption[];
    loading: boolean;
    variant: FilterVariant;
    onSelect: (value: string | string[], label?: string | string[]) => void;
    onSelectRange?: (range: [number, number]) => void;
    onSelectDateRange?: (startDate: string, endDate?: string) => void;
    /** Whether this filter should use multi-select mode */
    isMultiSelect?: boolean;
}

function FilterValueSelector<TData>({
    column,
    value,
    options,
    loading,
    variant,
    onSelect,
    onSelectRange,
    onSelectDateRange,
    isMultiSelect = false,
}: FilterValueSelectorProps<TData>) {
    // State for multi-select mode
    const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
    const [selectedLabels, setSelectedLabels] = React.useState<string[]>([]);
    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Boolean - static Yes/No
    if (variant === "boolean") {
        return (
            <CommandGroup>
                <CommandItem value="true" onSelect={() => onSelect("true")}>
                    Yes
                </CommandItem>
                <CommandItem value="false" onSelect={() => onSelect("false")}>
                    No
                </CommandItem>
            </CommandGroup>
        );
    }

    // Date - show range calendar picker with Apply button
    if (variant === "date" || variant === "dateRange") {
        const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
        const enableTime = !!meta?.enableTime;

        // Parse existing value to get date range
        const parseDate = (val: string | undefined): Date | undefined => {
            if (!val) return undefined;
            const d = new Date(val);
            return isNaN(d.getTime()) ? undefined : d;
        };

        // value might contain start date, we need to track range state
        const [dateRange, setDateRange] = React.useState<DateRange | undefined>(() => {
            const from = parseDate(value);
            return from ? { from, to: undefined } : undefined;
        });

        const [startTimeH, setStartTimeH] = React.useState(0);
        const [startTimeM, setStartTimeM] = React.useState(0);
        const [endTimeH, setEndTimeH] = React.useState(23);
        const [endTimeM, setEndTimeM] = React.useState(59);

        const formatDateStr = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const formatDateTimeStr = (date: Date, h: number, m: number): string => {
            return `${formatDateStr(date)}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
        };

        const handleApply = () => {
            if (!dateRange?.from) return;
            const startDate = enableTime ? formatDateTimeStr(dateRange.from, startTimeH, startTimeM) : formatDateStr(dateRange.from);
            const endDateObj = dateRange.to ?? dateRange.from;
            const endDate = enableTime ? formatDateTimeStr(endDateObj, endTimeH, endTimeM) : formatDateStr(endDateObj);

            if (onSelectDateRange) {
                onSelectDateRange(startDate, endDate);
            } else {
                onSelect(startDate);
            }
        };

        const canApply = !!dateRange?.from;

        return (
            <div className="p-2">
                {enableTime && (
                    <div className="flex flex-col gap-2 mb-2 pb-2 border-b border-border">
                        <TimeDropdownRow label="From" h24={startTimeH} min={startTimeM} onHourChange={setStartTimeH} onMinuteChange={setStartTimeM} />
                        <TimeDropdownRow label="To" h24={endTimeH} min={endTimeM} onHourChange={setEndTimeH} onMinuteChange={setEndTimeM} />
                    </div>
                )}
                <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={setDateRange}
                    captionLayout="dropdown"
                    fromYear={1900}
                    toYear={2100}
                    width={enableTime ? "100%" : undefined}
                    initialFocus
                    numberOfMonths={1}
                />
                <p className="text-xs text-muted-foreground mt-2 px-1">
                    {dateRange?.from && dateRange?.to
                        ? `${formatDateStr(dateRange.from)} to ${formatDateStr(dateRange.to)}`
                        : dateRange?.from
                            ? `${formatDateStr(dateRange.from)} (select end date or apply for single day)`
                            : "Select date range"}
                </p>
                <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={handleApply}
                    disabled={!canApply}
                >
                    Apply
                </Button>
            </div>
        );
    }

    // Range - show slider + apply
    if (variant === "range") {
        return (
            <RangeValueSelector
                column={column}
                onApply={(range) => onSelectRange?.(range)}
            />
        )
    }

    // If we have options, show them (with multi-select support)
    if (options.length > 0) {
        // Multi-select mode: show checkboxes with Apply button
        if (isMultiSelect) {
            const handleToggle = (optionValue: string, optionLabel: string) => {
                const idx = selectedValues.indexOf(optionValue);
                if (idx >= 0) {
                    // Remove
                    setSelectedValues(prev => prev.filter(v => v !== optionValue));
                    setSelectedLabels(prev => prev.filter((_, i) => i !== idx));
                } else {
                    // Add
                    setSelectedValues(prev => [...prev, optionValue]);
                    setSelectedLabels(prev => [...prev, optionLabel]);
                }
            };

            const handleApply = () => {
                if (selectedValues.length > 0) {
                    onSelect(selectedValues, selectedLabels);
                }
            };

            return (
                <div className="flex flex-col">
                    <Command style={{ backgroundColor: 'white' }}>
                        <CommandInput placeholder="Search..." />
                        <CommandList className="max-h-48">
                            <CommandEmpty>No options found.</CommandEmpty>
                            <CommandGroup>
                                {options.map((option) => {
                                    const isSelected = selectedValues.includes(option.value);
                                    return (
                                        <CommandItem
                                            key={option.value}
                                            value={option.label}
                                            onSelect={() => handleToggle(option.value, option.label)}
                                        >
                                            <div className={cn(
                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                            )}>
                                                <Check className="h-3 w-3" />
                                            </div>
                                            <span className="truncate flex-1">{option.label}</span>
                                            {option.count !== undefined && (
                                                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                                    {option.count}
                                                </span>
                                            )}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                    <div className="p-2 border-t">
                        <Button
                            size="sm"
                            className="w-full"
                            onClick={handleApply}
                            disabled={selectedValues.length === 0}
                        >
                            Apply {selectedValues.length > 0 && `(${selectedValues.length})`}
                        </Button>
                    </div>
                </div>
            );
        }

        // Single-select mode: simple list
        return (
            <CommandGroup>
                {options.map((option) => (
                    <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => onSelect(option.value, option.label)}
                    >
                        <span className="truncate flex-1">{option.label}</span>
                        {option.count !== undefined && (
                            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                {option.count}
                            </span>
                        )}
                    </CommandItem>
                ))}
            </CommandGroup>
        );
    }

    // Default: text input mode (type to filter)
    const isEmpty = !value.trim();

    return (
        <CommandGroup>
            <CommandItem
                value={value}
                onSelect={() => onSelect(value)}
                disabled={isEmpty}
            >
                {isEmpty ? (
                    <>
                        <Text className="text-muted-foreground" />
                        <span className="text-muted-foreground">Type to add filter...</span>
                    </>
                ) : (
                    <>
                        <BadgeCheck className="text-primary" />
                        <span className="truncate">Filter by &quot;{value}&quot;</span>
                    </>
                )}
            </CommandItem>
        </CommandGroup>
    );
}

/**
 * Min/Max dollar inputs for the range variant in FilterOptionsPanel.
 * - When column meta has `showEnrollment: true`, shows quick
 *   "Enrolled" / "Not enrolled" buttons with counts.
 * - When column meta has `isContribution: true`, shows a dimension
 *   selector (Total / Employer / Employee) so users can filter by
 *   employer pay or employee pay specifically.
 *
 * Dimension is encoded in selection values with `|` separator:
 *   "enrolled"           → total > 0
 *   "enrolled|employer"  → employer > 0
 *   "1000,5000|employee" → employee pay between $1K–$5K
 */
function RangeInputPanel<TData>({
    label,
    column,
    selectedValues,
    onSelectionChange,
}: {
    label: string;
    column?: Column<TData, unknown>;
    selectedValues: string[];
    onSelectionChange: (values: string[], labels: string[]) => void;
}) {
    const meta = column?.columnDef.meta as Record<string, unknown> | undefined;
    const showEnrollment = !!meta?.showEnrollment;
    const isContribution = !!meta?.isContribution;

    // Dimension selector state (total / employer / employee)
    type Dimension = "total" | "employer" | "employee";
    const DIMENSION_LABELS: Record<Dimension, string> = { total: "Total", employer: "Employer Pay", employee: "Employee Pay" };

    // Parse existing dimension from selection value ("value|dimension")
    const parseDimension = (val: string): { raw: string; dim: Dimension } => {
        const idx = val.lastIndexOf("|");
        if (idx === -1) return { raw: val, dim: "total" };
        const dimStr = val.slice(idx + 1);
        if (dimStr === "employer" || dimStr === "employee") return { raw: val.slice(0, idx), dim: dimStr };
        return { raw: val, dim: "total" };
    };

    const currentParsed = selectedValues[0] ? parseDimension(selectedValues[0]) : null;
    const [dimension, setDimension] = React.useState<Dimension>(currentParsed?.dim ?? "total");

    // Optional dimension value extractor passed via column meta
    const getDimensionValue = meta?.getDimensionValue as ((row: any, dim: string) => number) | undefined;

    // Compute enrollment counts from faceted row model (excludes own filter)
    // Counts update when dimension changes (e.g. switching to Employer Pay)
    const counts = React.useMemo(() => {
        if (!showEnrollment || !column) return null;
        try {
            const rows = column.getFacetedRowModel().rows;
            let enrolled = 0;
            let notEnrolled = 0;
            for (const row of rows) {
                let val: number;
                if (getDimensionValue && dimension !== "total") {
                    val = getDimensionValue(row.original, dimension);
                } else {
                    const cellVal = row.getValue(column.id);
                    val = typeof cellVal === "number" ? cellVal : 0;
                }
                if (val > 0) enrolled++;
                else notEnrolled++;
            }
            return { enrolled, notEnrolled };
        } catch {
            return null;
        }
    }, [showEnrollment, column, dimension, getDimensionValue]);

    // Parse existing selection
    const rawCurrent = currentParsed?.raw ?? "";
    const isEnrolledSel = rawCurrent === "enrolled" || rawCurrent === "not_enrolled";
    const isEnrolledType = rawCurrent === "enrolled";
    const isNotEnrolledType = rawCurrent === "not_enrolled";
    const rangeMatch = rawCurrent.includes(",") ? rawCurrent.split(",").map(Number) : null;

    const [minVal, setMinVal] = React.useState<string>(
        rangeMatch?.[0] && !Number.isNaN(rangeMatch[0]) ? String(rangeMatch[0]) : ""
    );
    const [maxVal, setMaxVal] = React.useState<string>(
        rangeMatch?.[1] && !Number.isNaN(rangeMatch[1]) ? String(rangeMatch[1]) : ""
    );

    /** Append dimension suffix to a value string when not "total". */
    const withDim = (val: string, dim: Dimension) => dim === "total" ? val : `${val}|${dim}`;

    /** Build a display label for the dimension. */
    const dimLabel = (baseLbl: string, dim: Dimension) =>
        dim === "total" ? baseLbl : `${baseLbl} (${DIMENSION_LABELS[dim]})`;

    const handleEnrollment = (type: "enrolled" | "not_enrolled") => {
        const baseLbl = type === "enrolled" ? "Enrolled" : "Not enrolled";
        // Toggle off if already selected with same type
        if ((type === "enrolled" && isEnrolledType && currentParsed?.dim === dimension)
            || (type === "not_enrolled" && isNotEnrolledType && currentParsed?.dim === dimension)) {
            onSelectionChange([], []);
        } else {
            onSelectionChange([withDim(type, dimension)], [dimLabel(baseLbl, dimension)]);
        }
        setMinVal("");
        setMaxVal("");
    };

    const handleApplyRange = () => {
        const min = Number(minVal) || 0;
        const max = Number(maxVal) || 0;
        if (min === 0 && max === 0) {
            onSelectionChange([], []);
            return;
        }
        const fmt = (n: number) =>
            n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
        const baseLbl = max > 0 ? `${fmt(min)} – ${fmt(max)}` : `≥ ${fmt(min)}`;
        onSelectionChange([withDim(`${min},${max}`, dimension)], [dimLabel(baseLbl, dimension)]);
    };

    const handleClear = () => {
        setMinVal("");
        setMaxVal("");
        onSelectionChange([], []);
    };

    return (
        <div className="w-[260px] flex-shrink-0 flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="font-medium text-sm truncate">{label}</span>
                {selectedValues.length > 0 && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="text-xs text-muted-foreground hover:text-foreground flex-shrink-0 ml-2"
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* Dimension selector for contribution pairs (ER / EE) */}
            {showEnrollment && isContribution && (
                <div className="px-3 pt-2">
                    <div className="flex gap-0.5 p-0.5 bg-muted rounded-md">
                        {(["total", "employer", "employee"] as const).map((d) => (
                            <button
                                key={d}
                                type="button"
                                className={cn(
                                    "flex-1 px-2 py-1 text-xs rounded-sm transition-colors cursor-pointer",
                                    dimension === d ? "bg-white shadow-sm font-medium" : "hover:bg-white/50"
                                )}
                                onClick={() => setDimension(d)}
                            >
                                {DIMENSION_LABELS[d]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Enrollment quick-filters (benefits) */}
            {showEnrollment && counts && (
                <div className="px-3 pt-2 pb-1 space-y-0.5">
                    <button
                        type="button"
                        className={cn(
                            "flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors cursor-pointer",
                            isEnrolledType ? "bg-primary/10 text-primary" : "hover:bg-accent"
                        )}
                        onClick={() => handleEnrollment("enrolled")}
                    >
                        <Checkbox checked={isEnrolledType} className="pointer-events-none" tabIndex={-1} />
                        <span className="flex-1 text-left">Enrolled</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{counts.enrolled}</span>
                    </button>
                    <button
                        type="button"
                        className={cn(
                            "flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors cursor-pointer",
                            isNotEnrolledType ? "bg-primary/10 text-primary" : "hover:bg-accent"
                        )}
                        onClick={() => handleEnrollment("not_enrolled")}
                    >
                        <Checkbox checked={isNotEnrolledType} className="pointer-events-none" tabIndex={-1} />
                        <span className="flex-1 text-left">Not enrolled</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{counts.notEnrolled}</span>
                    </button>
                    <div className="border-b pt-1.5" />
                    <p className="text-[11px] text-muted-foreground pt-1.5 pb-0.5">Or filter by amount</p>
                </div>
            )}

            {/* Min / Max inputs */}
            <div className="p-3 space-y-3">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                            autoFocus={!showEnrollment}
                            type="number"
                            inputMode="numeric"
                            placeholder="Min"
                            value={minVal}
                            onChange={(e) => setMinVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleApplyRange(); }}
                            className="h-8 pl-6"
                        />
                    </div>
                    <span className="text-muted-foreground text-xs">to</span>
                    <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="Max"
                            value={maxVal}
                            onChange={(e) => setMaxVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleApplyRange(); }}
                            className="h-8 pl-6"
                        />
                    </div>
                </div>
                <Button size="sm" className="w-full" onClick={handleApplyRange}>
                    Apply
                </Button>
            </div>
        </div>
    );
}

function RangeValueSelector<TData>({
    column,
    onApply,
}: {
    column: Column<TData, unknown>
    onApply: (range: [number, number]) => void
}) {
    const meta = column.columnDef.meta as { range?: [number, number]; unit?: string; label?: string } | undefined
    const defaultRange: [number, number] = meta?.range ?? [0, 100]
    const unit = meta?.unit

    const [rangeValue, setRangeValue] = React.useState<[number, number]>(defaultRange)

    React.useEffect(() => {
        setRangeValue(defaultRange)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [column.id])

    return (
        <div className="p-3 space-y-3 min-w-[280px]">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{meta?.label ?? column.id}</span>
                <span>
                    {rangeValue[0]}–{rangeValue[1]}{unit ? ` ${unit}` : ""}
                </span>
            </div>

            <Slider
                value={rangeValue}
                min={defaultRange[0]}
                max={defaultRange[1]}
                step={1}
                onValueChange={(val) => {
                    if (Array.isArray(val) && val.length === 2) {
                        setRangeValue([val[0] ?? defaultRange[0], val[1] ?? defaultRange[1]])
                    }
                }}
            />

            <div className="flex items-center gap-2">
                <Input
                    type="number"
                    inputMode="numeric"
                    value={rangeValue[0]}
                    onChange={(e) => {
                        const v = Number(e.target.value)
                        if (!Number.isNaN(v)) setRangeValue([v, rangeValue[1]])
                    }}
                    className="h-8"
                />
                <span className="text-muted-foreground text-xs">to</span>
                <Input
                    type="number"
                    inputMode="numeric"
                    value={rangeValue[1]}
                    onChange={(e) => {
                        const v = Number(e.target.value)
                        if (!Number.isNaN(v)) setRangeValue([rangeValue[0], v])
                    }}
                    className="h-8"
                />
            </div>

            <Button
                size="sm"
                className="w-full"
                onClick={() => onApply(rangeValue)}
            >
                Apply
            </Button>
        </div>
    )
}

// ============================================================================
// Filter Item (pill for active filters)
// ============================================================================

interface CommandFilterItemProps<TData> {
    filter: ColumnFilter;
    filterItemId: string;
    columns: Column<TData>[];
    columnLabels: Map<string, string>;
    columnVariants: Map<string, FilterVariant>;
    table: Table<TData>;
    onFilterUpdate: (filterId: string, updates: Partial<ColumnFilter>) => void;
    onFilterRemove: (filterId: string) => void;
    onFetchOptions?: FetchOptionsCallback;
    columnToFieldMap: Record<string, string>;
}

function CommandFilterItem<TData>({
    filter,
    filterItemId,
    columns,
    columnLabels,
    columnVariants,
    table,
    onFilterUpdate,
    onFilterRemove,
    onFetchOptions,
    columnToFieldMap,
}: CommandFilterItemProps<TData>) {
    const [showValueSelector, setShowValueSelector] = React.useState(false);

    const column = columns.find(c => c.id === filter.id);
    const inputId = `${filterItemId}-input`;

    const variant = (columnVariants.get(filter.id) ?? "text") as FilterVariant;
    const filterValue = filter.value as FilterValue | undefined;
    const operator = filterValue?.operator ?? getDefaultOperator(variant);

    const onValueChange = React.useCallback(
        (newValue: string | number | string[] | undefined, label?: string | string[]) => {
            onFilterUpdate(filter.id, {
                value: {
                    operator,
                    value: newValue,
                    endValue: filterValue?.endValue,
                    label, // Store label(s) for display (e.g., user names for manager filter)
                },
            });
        },
        [filter.id, operator, filterValue?.endValue, onFilterUpdate]
    );

    const onEndValueChange = React.useCallback(
        (newEndValue: string | number | undefined) => {
            onFilterUpdate(filter.id, {
                value: {
                    operator,
                    value: filterValue?.value,
                    endValue: newEndValue,
                    label: filterValue?.label,
                },
            });
        },
        [filter.id, operator, filterValue?.value, filterValue?.label, onFilterUpdate]
    );

    const onItemKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement
            ) {
                return;
            }

            // Don't remove filter if value selector is open
            if (showValueSelector) {
                return;
            }

            if (REMOVE_FILTER_SHORTCUTS.includes(event.key.toLowerCase())) {
                event.preventDefault();
                onFilterRemove(filter.id);
            }
        },
        [filter.id, showValueSelector, onFilterRemove]
    );

    // Get display value - use label if available (for select/id fields)
    // IMPORTANT: This useMemo must be called BEFORE any early returns to satisfy React's Rules of Hooks
    const displayValue = React.useMemo(() => {
        const val = filterValue?.value;
        const label = filterValue?.label;

        if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
            return "...";
        }

        // If we have labels stored, use them (for select/id fields like manager)
        if (label) {
            if (Array.isArray(label)) {
                if (label.length === 0) return "...";
                if (label.length <= MAX_VISIBLE_LABELS) return label.join(", ");
                const visible = label.slice(0, MAX_VISIBLE_LABELS).join(", ");
                return `${visible} +${label.length - MAX_VISIBLE_LABELS}`;
            }
            return label;
        }

        // Handle array values without labels
        if (Array.isArray(val)) {
            if (val.length === 0) return "...";
            if (val.length <= MAX_VISIBLE_LABELS) return (val as string[]).map(String).join(", ");
            const visible = (val as string[]).slice(0, MAX_VISIBLE_LABELS).map(String).join(", ");
            return `${visible} +${val.length - MAX_VISIBLE_LABELS}`;
        }

        if (variant === "boolean") {
            return val === "true" ? "Yes" : "No";
        }
        if (variant === "date" || variant === "dateRange") {
            try {
                const startDate = formatDate(new Date(val as string), { month: "short" });
                const endVal = filterValue?.endValue;
                if (endVal) {
                    const endDate = formatDate(new Date(endVal as string), { month: "short" });
                    return `${startDate} - ${endDate}`;
                }
                return startDate;
            } catch {
                return String(val);
            }
        }
        return String(val);
    }, [filterValue?.value, filterValue?.label, filterValue?.endValue, variant]);

    // Full display value for tooltip (all labels/values joined, no "+N")
    const displayValueFull = React.useMemo(() => {
        const val = filterValue?.value;
        const label = filterValue?.label;
        if (label && Array.isArray(label)) return label.join(", ");
        if (Array.isArray(val)) return (val as string[]).map(String).join(", ");
        return displayValue;
    }, [filterValue?.value, filterValue?.label, displayValue]);

    // Get a display-friendly label for the filter field
    // Use columnLabels if available, otherwise check for stored fieldLabel in filter value
    const getFilterFieldLabel = () => {
        // First try column labels (for known columns)
        const label = columnLabels.get(filter.id);
        if (label) return label;

        // Check if fieldLabel is stored in the filter value (for externally-set filters)
        const storedFieldLabel = (filterValue as FilterValue & { fieldLabel?: string })?.fieldLabel;
        if (storedFieldLabel) return storedFieldLabel;

        // For dynamic scope columns (scope_xxx), try to find the scope name from allFilterableColumns
        if (filter.id.startsWith('scope_')) {
            const scopeColumn = columns.find(c => c.id === filter.id);
            if (scopeColumn) {
                const meta = scopeColumn.columnDef.meta as CommandFilterColumnMeta | undefined;
                if (meta?.label) return meta.label;
            }
        }

        // Fall back to filter ID
        return filter.id;
    };

    // Render chip even if column not found - allows externally-set filters to show
    // The filter will still work, just with limited editing capabilities
    const fieldLabel = getFilterFieldLabel();

    // Get values and labels as arrays for multi-value display
    const valuesArray = React.useMemo(() => {
        const val = filterValue?.value;
        if (!val) return [];
        if (Array.isArray(val)) return val as string[];
        return [String(val)];
    }, [filterValue?.value]);

    const labelsArray = React.useMemo(() => {
        const lbl = filterValue?.label;
        if (!lbl) return valuesArray; // Fall back to values if no labels
        if (Array.isArray(lbl)) return lbl;
        return [lbl];
    }, [filterValue?.label, valuesArray]);

    // Check if this is a multi-select filter (has array values or is select/scope type)
    const isMultiSelect = React.useMemo(() => {
        const meta = column?.columnDef.meta as CommandFilterColumnMeta | undefined;
        return meta?.supportsOptions || meta?.options || variant === "boolean" || Array.isArray(filterValue?.value);
    }, [column, variant, filterValue?.value]);

    // Handle removing a single value from multi-select
    const onRemoveSingleValue = React.useCallback((valueToRemove: string, labelToRemove: string) => {
        const newValues = valuesArray.filter(v => v !== valueToRemove);
        const newLabels = labelsArray.filter(l => l !== labelToRemove);

        if (newValues.length === 0) {
            // No values left, remove the entire filter
            onFilterRemove(filter.id);
        } else {
            onValueChange(newValues, newLabels);
        }
    }, [filter.id, valuesArray, labelsArray, onFilterRemove, onValueChange]);

    // For multi-select filters with values, show individual badges
    // For text/number/date, show the inline editor
    const showValueBadges = isMultiSelect && valuesArray.length > 0;

    // Max badges to show before showing +N
    const MAX_VISIBLE_BADGES = 2;

    // Improved chip design with individual value badges
    // - Field label styled as muted text
    // - Each value shown as a removable badge with tooltip
    // - Click chevron to edit/add more values
    return (
        <div
            role="listitem"
            id={filterItemId}
            className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 dark:bg-input/30"
            onKeyDown={onItemKeyDown}
        >
            {/* Field label - muted, smaller */}
            <span className="text-xs text-muted-foreground font-medium select-none whitespace-nowrap">
                {fieldLabel}
            </span>

            {showValueBadges ? (
                <>
                    {/* Individual value badges with design system colors */}
                    <div className="flex items-center gap-1">
                        {valuesArray.slice(0, MAX_VISIBLE_BADGES).map((value, index) => {
                            const label = labelsArray[index] ?? value;
                            const isTruncated = label.length > 12;

                            const badgeContent = (
                                <span
                                    key={value}
                                    className="inline-flex items-center gap-0.5 bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs font-medium group"
                                >
                                    <span className="max-w-[80px] truncate">{label}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveSingleValue(value, label);
                                        }}
                                        className="ml-0.5 rounded-sm opacity-60 hover:opacity-100 hover:bg-secondary-foreground/10 p-0.5 transition-opacity"
                                        aria-label={`Remove ${label}`}
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                </span>
                            );

                            // Wrap in tooltip if text is truncated
                            if (isTruncated) {
                                return (
                                    <Tooltip key={value}>
                                        <TooltipTrigger asChild>
                                            {badgeContent}
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="max-w-xs">
                                            {label}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            }
                            return badgeContent;
                        })}

                        {/* Show +N if more values exist */}
                        {valuesArray.length > MAX_VISIBLE_BADGES && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex items-center bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-xs font-medium cursor-default">
                                        +{valuesArray.length - MAX_VISIBLE_BADGES}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <div className="text-sm">
                                        {labelsArray.slice(MAX_VISIBLE_BADGES).join(", ")}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>

                    {/* Edit button to modify selections */}
                    {column && (
                        <Popover open={showValueSelector} onOpenChange={setShowValueSelector}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="rounded p-0.5 hover:bg-muted text-muted-foreground transition-colors"
                                    aria-label="Edit filter values"
                                >
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-64 p-0 bg-white">
                                <CommandFilterValuePopover
                                    filter={filter}
                                    column={column}
                                    variant={variant}
                                    currentValues={valuesArray}
                                    currentLabels={labelsArray}
                                    onValueChange={onValueChange}
                                    onFetchOptions={onFetchOptions}
                                    columnToFieldMap={columnToFieldMap}
                                    onClose={() => setShowValueSelector(false)}
                                />
                            </PopoverContent>
                        </Popover>
                    )}
                </>
            ) : (
                <>
                    {/* Non-multi-select: show inline editor or value */}
                    {(variant === "date" || variant === "dateRange") ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs font-medium truncate max-w-40 select-none cursor-default">
                                    {displayValue}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{displayValueFull}</TooltipContent>
                        </Tooltip>
                    ) : column ? (
                        <CommandFilterValueEditor
                            filter={filter}
                            column={column}
                            inputId={inputId}
                            variant={variant}
                            operator={operator}
                            value={filterValue?.value}
                            endValue={filterValue?.endValue}
                            displayValue={displayValue}
                            displayValueFull={displayValueFull}
                            showValueSelector={showValueSelector}
                            setShowValueSelector={setShowValueSelector}
                            onValueChange={onValueChange}
                            onEndValueChange={onEndValueChange}
                            onFetchOptions={onFetchOptions}
                            columnToFieldMap={columnToFieldMap}
                            noBorder
                        />
                    ) : (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="text-sm truncate max-w-32">{displayValue}</span>
                            </TooltipTrigger>
                            <TooltipContent>{displayValueFull}</TooltipContent>
                        </Tooltip>
                    )}
                </>
            )}

            {/* Clear all button:
                - For multi-select with badges: show only if 2+ values (can clear single value via badge X)
                - For non-multi-select (text/date): always show since it's the only way to clear
            */}
            {(!showValueBadges || valuesArray.length >= 2) && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            aria-controls={filterItemId}
                            className="rounded p-0.5 opacity-60 hover:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                            onClick={() => onFilterRemove(filter.id)}
                            aria-label="Clear filter"
                        >
                            <X className="size-3.5" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        {showValueBadges ? "Clear all" : "Remove filter"}
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}

// ============================================================================
// Value Popover for editing multi-select filter values
// ============================================================================

interface CommandFilterValuePopoverProps<TData> {
    filter: ColumnFilter;
    column: Column<TData>;
    variant: FilterVariant;
    currentValues: string[];
    currentLabels: string[];
    onValueChange: (value: string[] | undefined, label?: string[]) => void;
    onFetchOptions?: FetchOptionsCallback;
    columnToFieldMap: Record<string, string>;
    onClose: () => void;
}

function CommandFilterValuePopover<TData>({
    column,
    variant,
    currentValues,
    currentLabels,
    onValueChange,
    onFetchOptions,
    columnToFieldMap,
}: CommandFilterValuePopoverProps<TData>) {
    const [options, setOptions] = React.useState<CommandFilterOption[]>([]);
    const [loadingOptions, setLoadingOptions] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");

    // Fetch options
    const fetchOptions = React.useCallback(async (query?: string) => {
        const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;

        if (variant === "boolean") {
            setOptions([
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
            ]);
            return;
        }

        if (!meta?.supportsOptions && meta?.options) {
            setOptions(meta.options.map(o => ({ value: o.value, label: o.label })));
            return;
        }

        if (meta?.supportsOptions && onFetchOptions) {
            setLoadingOptions(true);
            try {
                const backendField = columnToFieldMap[column.id] ?? column.id;
                const fetched = await onFetchOptions(backendField, query);
                setOptions(fetched);
            } catch (error) {
                console.error("Failed to fetch options:", error);
                setOptions([]);
            } finally {
                setLoadingOptions(false);
            }
        }
    }, [column, variant, onFetchOptions, columnToFieldMap]);

    // Debounced search
    const debouncedFetch = useDebouncedCallback((query: string) => {
        fetchOptions(query);
    }, FILTER_DEBOUNCE_MS);

    // Fetch on mount
    React.useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    // Handle toggle
    const handleToggle = React.useCallback((value: string, label: string) => {
        const isSelected = currentValues.includes(value);
        let newValues: string[];
        let newLabels: string[];

        if (isSelected) {
            newValues = currentValues.filter(v => v !== value);
            newLabels = currentLabels.filter(l => l !== label);
        } else {
            newValues = [...currentValues, value];
            newLabels = [...currentLabels, label];
        }

        onValueChange(newValues.length > 0 ? newValues : undefined, newLabels.length > 0 ? newLabels : undefined);
    }, [currentValues, currentLabels, onValueChange]);

    const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
    const supportsServerSearch = !!(meta?.supportsOptions && onFetchOptions);

    const handleSearch = React.useCallback((value: string) => {
        setSearchValue(value);
        if (supportsServerSearch) {
            debouncedFetch(value);
        }
    }, [supportsServerSearch, debouncedFetch]);

    // Client-side filter for static options
    const filteredOptions = React.useMemo(() => {
        if (!searchValue.trim() || supportsServerSearch) return options;
        const search = searchValue.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(search));
    }, [options, searchValue, supportsServerSearch]);

    // Workaround: Dialog's react-remove-scroll blocks wheel events on portaled popovers
    const scrollRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollHeight <= clientHeight) return;
            const maxScroll = scrollHeight - clientHeight;
            const newScroll = Math.max(0, Math.min(maxScroll, scrollTop + e.deltaY));
            if (newScroll !== scrollTop) {
                el.scrollTop = newScroll;
                e.preventDefault();
                e.stopPropagation();
            }
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    return (
        <div className="w-[260px] max-h-[min(400px,70vh)] flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="font-medium text-sm truncate">
                    {(column.columnDef.meta as CommandFilterColumnMeta | undefined)?.label ?? column.id}
                </span>
                {currentValues.length > 0 && (
                    <span className="text-xs text-primary font-medium flex-shrink-0 ml-2">
                        {currentValues.length} selected
                    </span>
                )}
            </div>

            {/* Search input */}
            <div className="border-b px-3 py-2">
                <Input
                    autoFocus
                    placeholder="Search..."
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="h-8 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none"
                />
            </div>

            {/* Options list */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto py-1">
                {loadingOptions ? (
                    <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : filteredOptions.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                        No options found.
                    </div>
                ) : (
                    filteredOptions.map((option) => {
                        const isSelected = currentValues.includes(option.value);
                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={cn(
                                    "flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer transition-colors",
                                    isSelected && "bg-accent/50"
                                )}
                                onClick={() => handleToggle(option.value, option.label)}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    className="pointer-events-none"
                                    tabIndex={-1}
                                />
                                <span className="truncate flex-1 text-left">{option.label}</span>
                                {option.count !== undefined && (
                                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                        {option.count}
                                    </span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Value Editor for Filter Items
// ============================================================================

interface CommandFilterValueEditorProps<TData> {
    filter: ColumnFilter;
    column: Column<TData>;
    inputId: string;
    variant: FilterVariant;
    operator: FilterOperator;
    value: string | number | string[] | undefined;
    endValue?: string | number | undefined;
    displayValue: string;
    displayValueFull?: string;
    showValueSelector: boolean;
    setShowValueSelector: (show: boolean) => void;
    onValueChange: (value: string | number | string[] | undefined, label?: string | string[]) => void;
    onEndValueChange?: (endValue: string | number | undefined) => void;
    onFetchOptions?: FetchOptionsCallback;
    columnToFieldMap: Record<string, string>;
    /** If true, removes border styling (when parent container has the border) */
    noBorder?: boolean;
}

function CommandFilterValueEditor<TData>({
    filter,
    column,
    inputId,
    variant,
    operator,
    value,
    endValue,
    displayValue,
    displayValueFull,
    showValueSelector,
    setShowValueSelector,
    onValueChange,
    onEndValueChange,
    onFetchOptions,
    columnToFieldMap,
    noBorder = false,
}: CommandFilterValueEditorProps<TData>) {
    const [localValue, setLocalValue] = React.useState(value);
    const [options, setOptions] = React.useState<CommandFilterOption[]>([]);
    const [loadingOptions, setLoadingOptions] = React.useState(false);

    // Sync local state
    React.useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Fetch options when selector opens
    const fetchOptions = React.useCallback(async () => {
        const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;

        if (variant === "boolean") {
            setOptions([
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
            ]);
            return;
        }

        if (!meta?.supportsOptions && meta?.options) {
            setOptions(meta.options.map(o => ({ value: o.value, label: o.label })));
            return;
        }

        if (meta?.supportsOptions && onFetchOptions) {
            setLoadingOptions(true);
            try {
                const backendField = columnToFieldMap[column.id] ?? column.id;
                const fetched = await onFetchOptions(backendField);
                setOptions(fetched);
            } catch (error) {
                console.error("Failed to fetch options:", error);
                setOptions([]);
            } finally {
                setLoadingOptions(false);
            }
        }
    }, [column, variant, onFetchOptions, columnToFieldMap]);

    // Debounced value change
    const debouncedOnChange = useDebouncedCallback(
        (newValue: string | number | string[] | undefined) => {
            onValueChange(newValue);
        },
        FILTER_DEBOUNCE_MS
    );

    // IMPORTANT: All useMemo hooks must be called unconditionally before any early returns
    // to satisfy React's Rules of Hooks

    // Get current selected values as array (for select with options)
    const selectedValues: string[] = React.useMemo(() => {
        if (!localValue) return [];
        if (Array.isArray(localValue)) return localValue as string[];
        return [String(localValue)];
    }, [localValue]);

    // Get current labels as array (from filterValue)
    const currentLabels: string[] = React.useMemo(() => {
        const lbl = (filter.value as FilterValue | undefined)?.label;
        if (!lbl) return [];
        if (Array.isArray(lbl)) return lbl;
        return [lbl];
    }, [filter.value]);

    // Build a map of value -> label from options for quick lookup
    const optionLabelMap = React.useMemo(() => {
        const map = new Map<string, string>();
        options.forEach(opt => map.set(opt.value, opt.label));
        return map;
    }, [options]);

    // Check if operator needs no value
    if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
        return (
            <div
                id={inputId}
                role="status"
                className={cn(
                    "h-8 w-16 rounded-none bg-transparent px-1.5 py-0.5 text-muted-foreground",
                    !noBorder && "border dark:bg-input/30"
                )}
            />
        );
    }

    // Number input
    if (variant === "number") {
        return (
            <Input
                id={inputId}
                type="number"
                inputMode="numeric"
                placeholder="Value"
                value={(localValue as number | undefined) ?? ""}
                onChange={(event) => {
                    const val = event.target.value;
                    const newValue = val === "" ? undefined : Number(val);
                    setLocalValue(newValue);
                    debouncedOnChange(newValue);
                }}
                className={cn("h-8 w-20 rounded-none px-1.5", !noBorder && "border")}
            />
        );
    }

    // Date input - use single date picker for most operators, range picker only for isBetween
    if (variant === "date" || variant === "dateRange") {
        const editorMeta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
        const enableTime = !!editorMeta?.enableTime;
        const isBetween = operator === "isBetween";

        const parseDate = (val: string | number | undefined): Date | undefined => {
            if (!val) return undefined;
            const d = new Date(val);
            return isNaN(d.getTime()) ? undefined : d;
        };

        const parseTimeFromVal = (val: string | number | undefined, fallbackH: number, fallbackM: number): { h: number; m: number } => {
            if (!val || typeof val !== 'string' || !val.includes('T')) return { h: fallbackH, m: fallbackM };
            const timePart = val.split('T')[1];
            if (!timePart) return { h: fallbackH, m: fallbackM };
            const [hStr, mStr] = timePart.split(':');
            return { h: parseInt(hStr, 10) || fallbackH, m: parseInt(mStr, 10) || fallbackM };
        };

        const startDate = parseDate(localValue as string | number | undefined);
        const endDate = parseDate(endValue);

        const [sTimeH, setSTimeH] = React.useState(() => parseTimeFromVal(localValue as string | undefined, 0, 0).h);
        const [sTimeM, setSTimeM] = React.useState(() => parseTimeFromVal(localValue as string | undefined, 0, 0).m);
        const [eTimeH, setETimeH] = React.useState(() => parseTimeFromVal(endValue as string | undefined, 23, 59).h);
        const [eTimeM, setETimeM] = React.useState(() => parseTimeFromVal(endValue as string | undefined, 23, 59).m);

        const formatDateStr = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const formatDateTimeStr = (date: Date, h: number, m: number): string => {
            return `${formatDateStr(date)}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
        };

        const emitDateValue = (date: Date | undefined, h: number, m: number) => {
            if (!date) { setLocalValue(undefined); onValueChange(undefined); return; }
            const v = enableTime ? formatDateTimeStr(date, h, m) : formatDateStr(date);
            setLocalValue(v);
            onValueChange(v);
        };

        const emitEndDateValue = (date: Date | undefined, h: number, m: number) => {
            if (!date) { onEndValueChange?.(undefined); return; }
            const v = enableTime ? formatDateTimeStr(date, h, m) : formatDateStr(date);
            onEndValueChange?.(v);
        };

        // For "isBetween" operator, use range picker
        if (isBetween) {
            return (
                <Popover open={showValueSelector} onOpenChange={setShowValueSelector}>
                    <PopoverTrigger asChild>
                        <Button
                            id={inputId}
                            variant="ghost"
                            size="sm"
                            className={cn(
                                "h-full rounded-none px-1.5 font-normal",
                                !noBorder && "border dark:bg-input/30",
                                !localValue && "text-muted-foreground"
                            )}
                        >
                            <CalendarIcon className="size-3.5" />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="truncate">{displayValue}</span>
                                </TooltipTrigger>
                                <TooltipContent>{displayValueFull ?? displayValue}</TooltipContent>
                            </Tooltip>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0 bg-white">
                        {enableTime && (
                            <div className="flex flex-col gap-2 p-2 pb-0 border-b border-border mb-0">
                                <TimeDropdownRow label="From" h24={sTimeH} min={sTimeM} onHourChange={(h) => { setSTimeH(h); if (startDate) emitDateValue(startDate, h, sTimeM); }} onMinuteChange={(m) => { setSTimeM(m); if (startDate) emitDateValue(startDate, sTimeH, m); }} />
                                <TimeDropdownRow label="To" h24={eTimeH} min={eTimeM} onHourChange={(h) => { setETimeH(h); if (endDate) emitEndDateValue(endDate, h, eTimeM); }} onMinuteChange={(m) => { setETimeM(m); if (endDate) emitEndDateValue(endDate, eTimeH, m); }} className="pb-2" />
                            </div>
                        )}
                        <Calendar
                            mode="range"
                            selected={{
                                from: startDate,
                                to: endDate,
                            }}
                            onSelect={(range) => {
                                if (!range?.from) {
                                    setLocalValue(undefined);
                                    onValueChange(undefined);
                                    onEndValueChange?.(undefined);
                                    return;
                                }
                                const newStart = enableTime ? formatDateTimeStr(range.from, sTimeH, sTimeM) : formatDateStr(range.from);
                                const newEnd = range.to ? (enableTime ? formatDateTimeStr(range.to, eTimeH, eTimeM) : formatDateStr(range.to)) : undefined;
                                setLocalValue(newStart);
                                onValueChange(newStart);
                                onEndValueChange?.(newEnd);
                            }}
                            captionLayout="dropdown"
                            fromYear={1900}
                            toYear={2100}
                            width={enableTime ? "100%" : undefined}
                            initialFocus
                            numberOfMonths={1}
                        />
                    </PopoverContent>
                </Popover>
            );
        }

        // For all other operators, use single date picker
        return (
            <Popover open={showValueSelector} onOpenChange={setShowValueSelector}>
                <PopoverTrigger asChild>
                    <Button
                        id={inputId}
                        variant="ghost"
                        size="sm"
                        className={cn(
                            "h-full rounded-none px-1.5 font-normal",
                            !noBorder && "border dark:bg-input/30",
                            !localValue && "text-muted-foreground"
                        )}
                    >
                        <CalendarIcon className="size-3.5" />
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="truncate">{displayValue}</span>
                            </TooltipTrigger>
                            <TooltipContent>{displayValueFull ?? displayValue}</TooltipContent>
                        </Tooltip>
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0 bg-white">
                    {enableTime && (
                        <div className="p-2 pb-0 border-b border-border">
                            <TimeDropdownRow label="Time" h24={sTimeH} min={sTimeM} onHourChange={(h) => { setSTimeH(h); if (startDate) emitDateValue(startDate, h, sTimeM); }} onMinuteChange={(m) => { setSTimeM(m); if (startDate) emitDateValue(startDate, sTimeH, m); }} className="pb-2" />
                        </div>
                    )}
                    <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => {
                            if (!date) {
                                setLocalValue(undefined);
                                onValueChange(undefined);
                                return;
                            }
                            emitDateValue(date, sTimeH, sTimeM);
                            if (!enableTime) setShowValueSelector(false);
                        }}
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={2100}
                        width={enableTime ? "100%" : undefined}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        );
    }

    // Boolean select
    if (variant === "boolean") {
        return (
            <Select
                value={String(localValue ?? "true")}
                onValueChange={(val) => {
                    setLocalValue(val);
                    onValueChange(val);
                }}
            >
                <SelectTrigger
                    id={inputId}
                    className="h-8 rounded-none px-1.5 [&_svg]:hidden"
                >
                    <SelectValue placeholder={displayValue} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                </SelectContent>
            </Select>
        );
    }

    // Select with options (for fields that support options)
    const meta = column.columnDef.meta as CommandFilterColumnMeta | undefined;
    if (meta?.supportsOptions || meta?.options) {
        // Check if multi-select mode (isAnyOf or isNoneOf operators)
        const isMultiSelect = operator === "isAnyOf" || operator === "isNoneOf";

        // selectedValues, currentLabels, and optionLabelMap are now computed at the top of the component

        const handleOptionToggle = (optionValue: string, optionLabel: string) => {
            if (isMultiSelect) {
                // Multi-select: toggle the option
                const currentValues = [...selectedValues];
                const currentLbls = [...currentLabels];
                const idx = currentValues.indexOf(optionValue);

                if (idx >= 0) {
                    // Remove
                    currentValues.splice(idx, 1);
                    currentLbls.splice(idx, 1);
                } else {
                    // Add
                    currentValues.push(optionValue);
                    currentLbls.push(optionLabel);
                }

                setLocalValue(currentValues);
                onValueChange(currentValues, currentLbls);
                // Don't close popover in multi-select mode
            } else {
                // Single select: just set the value
                setLocalValue(optionValue);
                onValueChange(optionValue, optionLabel);
                setShowValueSelector(false);
            }
        };

        return (
            <Popover
                open={showValueSelector}
                onOpenChange={(open) => {
                    setShowValueSelector(open);
                    if (open) {
                        fetchOptions();
                    }
                }}
            >
                <PopoverTrigger asChild>
                    <Button
                        id={inputId}
                        variant="ghost"
                        size="sm"
                        className={cn(
                            "h-full min-w-16 max-w-48 rounded-none px-1.5 font-normal",
                            !noBorder && "border dark:bg-input/30",
                            !localValue && "text-muted-foreground"
                        )}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="truncate">{displayValue}</span>
                            </TooltipTrigger>
                            <TooltipContent>{displayValueFull ?? displayValue}</TooltipContent>
                        </Tooltip>
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-0 bg-white">
                    {loadingOptions ? (
                        <div className="flex items-center justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <Command style={{ backgroundColor: 'white' }}>
                            <CommandInput placeholder="Search..." />
                            <CommandList className="max-h-64">
                                <CommandEmpty>No options found.</CommandEmpty>
                                <CommandGroup>
                                    {options.map((option) => {
                                        const isSelected = selectedValues.includes(option.value);
                                        return (
                                            <CommandItem
                                                key={option.value}
                                                value={option.label}
                                                onSelect={() => handleOptionToggle(option.value, option.label)}
                                            >
                                                <div className={cn(
                                                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                    isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                                )}>
                                                    <Check className="h-3 w-3" />
                                                </div>
                                                <span className="truncate flex-1">{option.label}</span>
                                                {option.count !== undefined && (
                                                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                                        {option.count}
                                                    </span>
                                                )}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    )}
                </PopoverContent>
            </Popover>
        );
    }

    // Default: text input
    return (
        <Input
            id={inputId}
            type="text"
            placeholder="Value"
            value={(localValue as string | undefined) ?? ""}
            onChange={(event) => {
                const val = event.target.value;
                setLocalValue(val);
                debouncedOnChange(val);
            }}
            className={cn("h-8 w-24 rounded-none px-1.5", !noBorder && "border")}
        />
    );
}